import type { IValueMapping } from '@atlas/types'
import { AxisCode, MappingSystem } from '@atlas/types'
import { Field, SectionCard } from '@atlas/ui-components'
import { Button, Combobox } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useBootstrapValues, useRemoveMarketName, useSetMarketName } from '@/hooks/useClassification'
import { geoOptions } from '@/lib/geo-options'

/**
 * The names a brand or product carries per market, and the control to add another.
 *
 * A market name is a `taxonomy_title` mapping scoped to a geography value, held on the
 * subject's AXIS VALUE — so this is identical for a brand and a product, and is shared
 * rather than written twice. What is edited is the mapping, never a catalog column.
 */
export function MarketNames({
	valueId,
	subject,
	defaultName,
	mappings,
}: {
	/** The axis value the names hang off — `brand.valueId` or `product.valueId`. */
	valueId: string
	/**
	 * What the reader is looking at, for the copy. A brand or a product on the Explore
	 * pages; any axis value on the taxonomy admin, which names its axis instead.
	 */
	subject: string
	/** The name used where no market overrides it, for the empty state. */
	defaultName: string
	/** Every mapping on the value; the taxonomy_title rows are picked out here. */
	mappings: IValueMapping[]
}) {
	const geos = useBootstrapValues(AxisCode.GEOGRAPHY)
	const setName = useSetMarketName(valueId)
	const removeName = useRemoveMarketName(valueId)
	const [geoValueId, setGeoValueId] = useState('')
	const [name, setName_] = useState('')

	// The raw rows, not `page.names`: those are resolved for display and drop the value id
	// the mapping API addresses a scope by.
	const rows = mappings.filter((m) => m.system === MappingSystem.TAXONOMY_TITLE)
	const labelOf = (id: string | null) => (id ? (geos.find((g) => g.valueId === id)?.label ?? id) : 'Worldwide')

	const add = () => {
		if (!name.trim()) return
		setName.mutate({ geoValueId: geoValueId || null, name: name.trim() }, { onSuccess: () => setName_('') })
	}

	return (
		<SectionCard title="Market names" description={`What this ${subject} is called where it is named differently.`}>
			{rows.length === 0 ? (
				<p className="text-ds-text-muted text-sm">
					No market names are recorded — the {subject} is called “{defaultName}” everywhere.
				</p>
			) : (
				rows.map((row) => (
					<div
						key={row.geoValueId ?? 'worldwide'}
						className="flex items-baseline justify-between gap-4 border-ds-border border-b py-2 last:border-0"
					>
						<span className="text-ds-text-muted text-xs">{labelOf(row.geoValueId)}</span>
						<span className="flex items-center gap-3">
							<span className="text-ds-text text-sm">{row.externalCode}</span>
							<button
								type="button"
								className="text-ds-danger text-xs hover:underline"
								onClick={() => removeName.mutate({ geoValueId: row.geoValueId })}
							>
								remove
							</button>
						</span>
					</div>
				))
			)}

			<div className="mt-3 flex flex-wrap items-end gap-2">
				{/* A Combobox, not a native select: the geography axis is 111 values deep in a
				    hierarchy, which a flat unsearchable list cannot present. Same control the
				    work-order form uses, so region → country reads the same way in both. */}
				<div className="min-w-52">
					<span className="mb-1 block text-ds-text-muted text-xs">Market</span>
					<Combobox
						aria-label="Market"
						options={geoOptions(geos, 'valueId')}
						value={geoValueId || null}
						onChange={(v) => setGeoValueId(v ?? '')}
						// The empty choice is the worldwide name — a real scope, not an unset field.
						placeholder="Worldwide"
					/>
				</div>
				<Field
					label="Name in that market"
					value={name}
					onChange={(e) => setName_(e.target.value)}
					className="min-w-60 flex-1"
				/>
				<Button type="button" className="rounded-ds-full" isDisabled={setName.isPending || !name.trim()} onPress={add}>
					{setName.isPending ? 'Saving…' : 'Add name'}
				</Button>
			</div>
		</SectionCard>
	)
}
